import React, { useEffect, Suspense, useCallback } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import i18n from 'i18n';

// components
import ApiLoading from 'shared/ApiLoading';
import DemoRegistration from 'demoRegistration/pages/DemoRegistrationPage';
import FaqPage from 'appExplainers/pages/FaqPage';
import LoginPage from 'auth/pages/LoginPage';
import Onboarding from 'onboarding/pages/OnboardingPage';
import PageContainer from 'layout/components/PageContainer';
import SharedDeviceLockoutManager from 'auth/components/SharedDeviceLockoutManager';
import IosInstallHint from 'pwa/components/IosInstallHint';

// context
import StyleProvider from 'theme/muiTheme';

// hooks
import { useUser, User } from 'shared/hooks/api/user';
import { SUPPORTED_LANGUAGES, normalizeLanguage } from 'i18n/languages';
import usePostHogIdentify from 'shared/hooks/usePostHogIdentify';
import useTrackStandaloneLaunch from 'pwa/hooks/useTrackStandaloneLaunch';
import usePushSubscriptionRefresh from 'pwa/hooks/usePushSubscriptionRefresh';

// helpers
import routesConfig from 'constants/routesConfig.json';
import { setOfficialLanguageFallback } from 'shared/helpers/dayjs';

// styles
import * as Container from 'layout/styles/PageLayout.style';
import { useQueryClient } from '@tanstack/react-query';
import useIsOnline from 'shared/hooks/useIsOnline';

interface AppContentProps {
  user: User | undefined;
  isLoading: boolean;
  handleDefaultLanguage: (override?: string | null) => void;
}

const AppContent: React.FC<AppContentProps> = ({
  user,
  isLoading,
  handleDefaultLanguage,
}) => {
  const location = useLocation();

  let path = location.pathname;

  if (path.startsWith(routesConfig.DEMO_REGISTRATION) && !user?.id) {
    if (path.endsWith('/')) {
      path = path.substring(0, path.length - 1);
    }
    const lastIndex = path.lastIndexOf('/');
    const languageCode = path.substring(lastIndex + 1, path.length);
    if (SUPPORTED_LANGUAGES.some((code) => code === languageCode)) {
      handleDefaultLanguage(languageCode);
    }

    return (
      <Container.ScreenContainer id="screen">
        <Container.MediumPageContainer>
          <DemoRegistration />
        </Container.MediumPageContainer>
      </Container.ScreenContainer>
    );
  }

  if (isLoading) return <ApiLoading />;
  if (!user) {
    if (path.startsWith(routesConfig.FAQ_PAGE)) {
      return (
        <Container.ScreenContainer id="screen">
          <Container.PageContainer inECoach={false}>
            <FaqPage />
          </Container.PageContainer>
        </Container.ScreenContainer>
      );
    }
    return <LoginPage />;
  }
  if (!user.is_demo && user.member?.onboarding_completed_at === null)
    return (
      <Container.ScreenContainer id="screen">
        <Container.PageContainer inECoach={false}>
          <Onboarding />
        </Container.PageContainer>
      </Container.ScreenContainer>
    );

  return (
    <Suspense fallback="loading">
      <SharedDeviceLockoutManager userId={user.id} />
      <PageContainer />
    </Suspense>
  );
};

function App(): React.ReactElement {
  const { data: user, isLoading } = useUser();
  const queryClient = useQueryClient();
  const isOnline = useIsOnline();

  useEffect(() => {
    if (!isOnline) return;
    queryClient.resumePausedMutations();
  }, [isOnline, queryClient]);

  useEffect(() => {
    const checkVersion = async (): Promise<void> => {
      try {
        const response = await fetch('/version.json', { cache: 'no-cache' });
        const { version } = await response.json();
        const currentVersion = window.localStorage.getItem('app-version');

        if (version !== currentVersion) {
          window.localStorage.setItem('app-version', version);
          window.location.reload();
        }
      } catch (error) {
        console.error('Version check failed:', error);
      }
    };

    checkVersion();
  }, []);

  const handleDefaultLanguage = useCallback(
    (override: string | null = null): void => {
      if (!override && !user) return;

      const next = normalizeLanguage(override ?? user?.language);
      i18n.changeLanguage(next);
      document.documentElement.lang = next;
    },
    [user]
  );

  useEffect(() => {
    handleDefaultLanguage();
  }, [handleDefaultLanguage, user]);

  useEffect(() => {
    setOfficialLanguageFallback(user?.official_language);
  }, [user?.official_language]);

  usePostHogIdentify();
  useTrackStandaloneLaunch();
  usePushSubscriptionRefresh(user?.id);

  return (
    <StyleProvider>
      <Router>
        <AppContent
          user={user}
          isLoading={isLoading}
          handleDefaultLanguage={handleDefaultLanguage}
        />
        <IosInstallHint />
      </Router>
    </StyleProvider>
  );
}

export default App;
